import { MDtoHTML } from "components/ui/MDtoHTML/MDtoHTML"
import { useCallback, useEffect, useRef, useState } from "react"
import { useChatStore } from "store/aiAgent.store"
import DOMPurify from "dompurify"
import { marked } from "marked"
import { createRoot } from "react-dom/client"
import { CollapseJSON } from "components/ui/collapse/CollapseJSON/CollapseJSON"

type Props = {
	content: string
	onReferenceClick?: (page: string, quotation: string | undefined) => void
	onParagraphReferenceClick?: (pid: string) => void
	streamingInProgress?: boolean
}

const PAGE_REFERENCE_REGEX = /\[PAGE-(\d+)\]/g
const PARAGRAPH_REFERENCE_REGEX = /\[pid-([a-f0-9]+)\]/g
const SERIES_RESULT_REGEX = /RESULT:\s*"""(.*?)"""/s

export const TransformStreamedData = ({
	content,
	onReferenceClick,
	onParagraphReferenceClick,
	streamingInProgress,
}: Props) => {
	const [sanitizedContent, setSanitizedContent] = useState("")
	const [pageQuotationsMap, setPageQuotationsMap] = useState<Record<string, string>>({})
	const [jsonDataSections, setJsonDataSections] = useState<Array<{ id: string; data: Record<string, unknown> }>>([])
	const { toolCallInfo } = useChatStore()
	const containerRef = useRef<HTMLDivElement>(null)

	// Create a mapping from PIDs to paragraph numbers based on toolCallInfo
	const createPidToNumberMapping = useCallback(
		(content: string): Record<string, number> => {
			const pidMatches = content.match(PARAGRAPH_REFERENCE_REGEX) || []
			const uniquePids = [...new Set(pidMatches.map((match) => match.slice(1, -1)))] // Remove brackets and get unique PIDs

			// Sort PIDs that exist in toolCallInfo by their page number and position
			const sortedPids = uniquePids
				.filter((pid) => toolCallInfo[pid]) // Only include PIDs that exist in toolCallInfo
				.sort((a, b) => {
					const dataA = toolCallInfo[a]
					const dataB = toolCallInfo[b]

					// First sort by page number
					if (dataA.page_n !== dataB.page_n) {
						return dataA.page_n - dataB.page_n
					}

					// Then sort by y coordinate (top to bottom)
					if (dataA.highlight_box && dataB.highlight_box) {
						return dataA.highlight_box[1] - dataB.highlight_box[1]
					}

					return 0
				})

			// Create mapping from PID to paragraph number
			const mapping: Record<string, number> = {}
			sortedPids.forEach((pid, index) => {
				mapping[pid] = index + 1
			})

			return mapping
		},
		[toolCallInfo],
	)

	// Extract quotations that appear before references
	const extractQuotationBeforeReference = useCallback(
		(content: string, referencePattern: RegExp): Record<string, string> => {
			const quotationMap: Record<string, string> = {}

			// Find all matches of the reference pattern
			const referenceMatches = [...content.matchAll(referencePattern)]

			referenceMatches.forEach((match) => {
				const referenceIndex = match.index!
				const referenceIdentifier = match[1] // PAGE or PID identifier

				// Look backwards from the reference to find the nearest quotation
				const beforeReference = content.substring(0, referenceIndex)

				// Find all quotations before this reference and get the last one
				const quotationMatches = [...beforeReference.matchAll(/"""(.*?)"""/g)]

				if (quotationMatches.length > 0) {
					// Get the last quotation before this reference
					const lastQuotationMatch = quotationMatches[quotationMatches.length - 1]
					const quotation = lastQuotationMatch[1].trim()

					// Create a unique key for this specific reference occurrence - use same format as click handler
					const uniqueKey = `${referenceIdentifier}_${referenceIndex}`
					quotationMap[uniqueKey] = quotation
				}
			})

			return quotationMap
		},
		[],
	)

	// Sanitize the markdown-converted HTML
	useEffect(() => {
		// Create PID to number mapping
		const pidToNumberMapping = createPidToNumberMapping(content)

		// Extract quotations for both reference types
		const pageQuotations = extractQuotationBeforeReference(content, PAGE_REFERENCE_REGEX)

		// Store quotations in state
		setPageQuotationsMap(pageQuotations)

		// Process content to replace references with buttons
		// We'll do this in a single pass to ensure consistency
		let processedContent = content

		// Replace PAGE references with buttons FIRST, before any other replacements
		// This maintains correct indices from the original content
		const pageMatches = [...content.matchAll(PAGE_REFERENCE_REGEX)]

		// Create a map to track unique quotations and assign consistent reference numbers
		const quotationToNumberMap: Record<string, number> = {}
		let nextReferenceNumber = 1

		// First pass: assign reference numbers to unique quotations
		pageMatches.forEach((match) => {
			const index = match.index!
			const page = match[1]
			const uniqueKey = `${page}_${index}`
			const quotation = pageQuotations[uniqueKey]

			if (quotation) {
				// If this quotation hasn't been seen before, assign it a new number
				if (!(quotation in quotationToNumberMap)) {
					quotationToNumberMap[quotation] = nextReferenceNumber++
				}
			} else {
				// If no quotation found, still assign a unique number
				const fallbackKey = uniqueKey
				if (!(fallbackKey in quotationToNumberMap)) {
					quotationToNumberMap[fallbackKey] = nextReferenceNumber++
				}
			}
		})

		// Second pass: replace references with buttons using consistent numbering
		pageMatches.reverse().forEach((match) => {
			const page = match[1]
			const index = match.index!
			const uniqueKey = `${page}_${index}`
			const quotation = pageQuotations[uniqueKey]

			// Get the reference number based on the quotation (or fallback to unique key)
			const referenceNumber = quotation ? quotationToNumberMap[quotation] : quotationToNumberMap[uniqueKey]

			const replacement = `<button class="referenceButton" data-page="${page}" data-offset="${index}">${referenceNumber}</button>`
			processedContent =
				processedContent.slice(0, index) + replacement + processedContent.slice(index + match[0].length)
		})

		// Replace result series with div with class "foundSeries" AFTER page references
		const seriesResultMatch = processedContent.match(SERIES_RESULT_REGEX)
		if (seriesResultMatch) {
			const seriesText = seriesResultMatch[0].replace(/"""/g, "'")
			processedContent = processedContent.replace(SERIES_RESULT_REGEX, `<div class="foundSeries">${seriesText}</div>`)
		}

		// Replace PID references with buttons
		const pidMatches = [...processedContent.matchAll(PARAGRAPH_REFERENCE_REGEX)]
		pidMatches.reverse().forEach((match) => {
			const pid = match[1]
			const fullPid = `pid-${pid}`
			const paragraphNumber = pidToNumberMapping[fullPid]
			const displayText = paragraphNumber ? `${paragraphNumber}` : "paragraph link"
			const index = match.index!
			const replacement = `<button class="paragraphReferenceButton" data-paragraph="${fullPid}">${displayText}</button>`
			processedContent =
				processedContent.slice(0, index) + replacement + processedContent.slice(index + match[0].length)
		})

		// Parse the processed content with marked and sanitize the output
		const parseContent = async () => {
			try {
				// Set marked options
				marked.setOptions({
					gfm: true,
					breaks: true,
				})

				const parsedContent = await marked.parse(processedContent)

				// Create a temporary container to parse and transform the HTML
				const tempDiv = document.createElement("div")
				tempDiv.innerHTML = parsedContent

				const jsonSections: Array<{ id: string; data: Record<string, unknown> }> = []

				// Find all code blocks with language-json class
				const jsonCodeBlocks = tempDiv.querySelectorAll("code.language-json")
				jsonCodeBlocks.forEach((codeBlock, index) => {
					try {
						// Skip parsing if streaming is in progress
						if (streamingInProgress) {
							return
						}

						// Get the JSON text content and clean it
						let jsonText = codeBlock.textContent || ""

						// Remove any HTML entities that might have been decoded
						jsonText = jsonText
							.replace(/&amp;/g, "&")
							.replace(/&lt;/g, "<")
							.replace(/&gt;/g, ">")
							.replace(/&quot;/g, '"')
							.replace(/&#39;/g, "'")
							.trim()

						// Parse JSON
						const jsonData = JSON.parse(jsonText)

						// Create a unique ID for this JSON section
						const jsonId = `json-collapse-${index}`
						jsonSections.push({ id: jsonId, data: jsonData })

						// Create a placeholder div that will be replaced with the Collapse component
						const placeholderDiv = document.createElement("div")
						placeholderDiv.className = "jsonCollapsePlaceholder"
						placeholderDiv.setAttribute("data-json-id", jsonId)

						// Replace the pre > code structure with the placeholder
						const preElement = codeBlock.parentElement
						if (preElement && preElement.tagName === "PRE") {
							preElement.replaceWith(placeholderDiv)
						}
					} catch (e) {
						// Silently skip errors during streaming
						if (!streamingInProgress) {
							console.error("Error parsing JSON in code block:", e)
						}
						// Keep original code block if parsing fails
					}
				})

				// Store JSON sections in state only if not streaming
				if (!streamingInProgress) {
					setJsonDataSections(jsonSections)
				}

				const cleaned = DOMPurify.sanitize(tempDiv.innerHTML, {
					ALLOWED_TAGS: [
						"b",
						"i",
						"em",
						"strong",
						"a",
						"p",
						"br",
						"ul",
						"ol",
						"li",
						"h1",
						"h2",
						"h3",
						"h4",
						"h5",
						"h6",
						"blockquote",
						"code",
						"pre",
						"button",
						"div",
					],
					ALLOWED_ATTR: [
						"href",
						"target",
						"rel",
						"data-page",
						"data-paragraph",
						"data-offset",
						"class",
						"data-json-id",
					],
				})
				setSanitizedContent(cleaned)
			} catch (e) {
				console.error("Error parsing content:", e)
				setSanitizedContent("")
			}
		}

		parseContent()
	}, [content, toolCallInfo, createPidToNumberMapping, extractQuotationBeforeReference, streamingInProgress])

	// Add event listener using event delegation
	useEffect(() => {
		const container = containerRef.current
		if (!container) return

		const handleClick = (e: MouseEvent) => {
			const target = e.target as HTMLElement
			if (target.classList.contains("referenceButton")) {
				const page = target.getAttribute("data-page")
				const offset = target.getAttribute("data-offset")
				if (page && onReferenceClick) {
					// Find the quotation for this specific reference occurrence
					let quotation: string | undefined
					if (offset) {
						const uniqueKey = `${page}_${offset}`
						quotation = pageQuotationsMap[uniqueKey]
					}
					onReferenceClick(page, quotation)
				}
			} else if (target.classList.contains("paragraphReferenceButton")) {
				const paragraph = target.getAttribute("data-paragraph")
				if (paragraph && onParagraphReferenceClick) {
					onParagraphReferenceClick(paragraph)
				}
			}
		}

		container.addEventListener("click", handleClick)

		// Cleanup
		return () => {
			container.removeEventListener("click", handleClick)
		}
	}, [onReferenceClick, onParagraphReferenceClick, pageQuotationsMap])

	// Render Collapse components for JSON data sections after the HTML is rendered
	useEffect(() => {
		if (!containerRef.current || jsonDataSections.length === 0) return

		const roots: ReturnType<typeof createRoot>[] = []

		jsonDataSections.forEach(({ id, data }) => {
			const placeholder = containerRef.current?.querySelector(`[data-json-id="${id}"]`)
			if (placeholder) {
				// Format JSON with proper indentation
				const formattedJson = JSON.stringify(data, null, 2)

				// Create collapse items with code tag inside
				const collapseItems = [
					{
						children: (
							<pre aria-label="Preformatted JSON code">
								<code>{formattedJson}</code>
							</pre>
						),
						label: "json",
					},
				]

				// Render Collapse component using React 18's createRoot
				const root = createRoot(placeholder)
				root.render(<CollapseJSON items={collapseItems} />)
				roots.push(root)
			}
		})

		// Cleanup function to unmount roots
		return () => {
			roots.forEach((root) => root.unmount())
		}
	}, [jsonDataSections])

	return (
		<div ref={containerRef}>
			<MDtoHTML sanitizedContent={sanitizedContent} />
		</div>
	)
}
